import {useMemo} from "react";
import {
    IGroupStep,
    IProtocolStep,
    IProtocolStepKind,
    TProtocolStepId,
} from "@/protocols/types/protocol.ts";

/**
 * Result of useProtocolStepOrdering hook containing ordered steps and step index map.
 */
export interface UseProtocolStepOrderingResult {
    /** All steps ordered for linear navigation (groups expanded with their members) */
    orderedSteps: IProtocolStep[];
    /** Maps each step ID to its position in the natural protocol order */
    stepIndexMap: Map<TProtocolStepId, number>;
    /** Maps each step ID to its parent group (if the step is a group member) */
    groupsByStepId: Record<TProtocolStepId, IProtocolStep>;
}

/**
 * Processes protocol steps to create ordered navigation list and step index map.
 *
 * This hook provides the canonical ordering for protocol steps that matches how
 * ProtocolRecorder orders steps:
 * - Steps are ordered by their position in the protocol's step_ids array
 * - GROUP steps expand their memberStepIds in order
 * - Group members are not duplicated in the top-level order
 *
 * @param steps - Array of IProtocolStep from the protocol (should be in protocol.step_ids order)
 */
export const useProtocolStepOrdering = (
    steps: IProtocolStep[] | undefined
): UseProtocolStepOrderingResult => {
    return useMemo(() => {
        if (!steps || steps.length === 0) {
            return {
                orderedSteps: [],
                stepIndexMap: new Map<TProtocolStepId, number>(),
                groupsByStepId: {}
            };
        }

        // First pass: collect all group member IDs
        const groupMembers = new Set<TProtocolStepId>();
        steps.forEach(step => {
            if (step.kind === IProtocolStepKind.GROUP) {
                const group = step as IGroupStep;
                group.memberStepIds?.forEach(id => {
                    groupMembers.add(id);
                });
            }
        });

        // Build ordered list: groups expanded with their members
        // This matches the backend's UNNEST...WITH ORDINALITY ordering
        const orderedStepIds: TProtocolStepId[] = [];
        steps.forEach(step => {
            if (step.kind === IProtocolStepKind.GROUP) {
                // Add the group itself
                orderedStepIds.push(step.id);

                // Then add all its members in order
                const group = step as IGroupStep;
                group.memberStepIds?.forEach(memberId => {
                    orderedStepIds.push(memberId);
                });
            } else if (!groupMembers.has(step.id)) {
                // Add standalone steps (not part of any group)
                orderedStepIds.push(step.id);
            }
            // Group members are skipped here - already added inside their group
        });

        // Convert IDs back to step objects
        const orderedSteps = orderedStepIds
            .map(id => steps.find(s => s.id === id))
            .filter((step): step is IProtocolStep => step !== undefined);

        // Build stepIndexMap for O(1) lookups
        const stepIndexMap = new Map<TProtocolStepId, number>();
        orderedStepIds.forEach((id, index) => {
            stepIndexMap.set(id, index);
        });

        // Build groupsById: maps each group step to itself
        const groupsById: Record<TProtocolStepId, IProtocolStep> = {};
        steps.filter(step => step.kind === IProtocolStepKind.GROUP).forEach(step => {
            groupsById[step.id] = step;
        });

        // Build groupsByStepId: maps each step to its parent group
        const groupsByStepId: Record<TProtocolStepId, IProtocolStep> = {};
        steps.forEach(step => {
            if (step.kind === IProtocolStepKind.GROUP) {
                const group = step as IGroupStep;
                group.memberStepIds?.forEach(id => {
                    groupsByStepId[id] = groupsById[group.id];
                });
            }
        });

        return {orderedSteps, stepIndexMap, groupsByStepId};
    }, [steps]);
}